import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Drawer,
  Box,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Divider,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DashboardIcon from '@mui/icons-material/Dashboard';
import AddIcon from '@mui/icons-material/Add';
import ListIcon from '@mui/icons-material/List';
import PersonIcon from '@mui/icons-material/Person';
import AdminIcon from '@mui/icons-material/AdminPanelSettings';
import ApiKeyIcon from '@mui/icons-material/VpnKey';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import SettingsIcon from '@mui/icons-material/Settings';
import UsersIcon from '@mui/icons-material/People';
import ProjectsIcon from '@mui/icons-material/FolderSpecial';
import PublicIcon from '@mui/icons-material/Public';
import FilesIcon from '@mui/icons-material/CloudDownload';
import IntegrityIcon from '@mui/icons-material/HealthAndSafety';
import BackupIcon from '@mui/icons-material/Backup';
import OrganizationIcon from '@mui/icons-material/Business';
import SystemStatusIcon from '@mui/icons-material/Speed';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import StorageIcon from '@mui/icons-material/Storage';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import StarIcon from '@mui/icons-material/Star';
import LogsIcon from '@mui/icons-material/Article';
import HistoryIcon from '@mui/icons-material/History';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import PluginReleasesIcon from '@mui/icons-material/SystemUpdateAlt';
import CalibrationIcon from '@mui/icons-material/Tune';
import { useAuth } from '../../contexts/AuthContext';
import { getAdminInterfaceConfig } from '../../utils/userCapabilities';
import ObservatoryIcon from '../icons/ObservatoryIcon';

const AdminDrawer = ({ open, onClose, memberships = [], orgMemberships = [] }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  // All three sections start collapsed so the drawer opens to a
  // compact list of headers; the user expands the section they want.
  const [adminOpen, setAdminOpen] = useState(false);
  const [userOpen, setUserOpen] = useState(false);
  const [reportingOpen, setReportingOpen] = useState(false);

  const adminConfig = getAdminInterfaceConfig(user, memberships, orgMemberships);

  const handleNav = (path) => {
    navigate(path);
    onClose();
  };

  const userItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
    { text: 'Submit Observation', icon: <AddIcon />, path: '/observations/new' },
    { text: 'Search Observations', icon: <SearchIcon />, path: '/observations/search' },
    { text: 'My Observations', icon: <ListIcon />, path: '/observations' },
    { text: 'My Files', icon: <FilesIcon />, path: '/files' },
    { text: 'My Projects', icon: <ProjectsIcon />, path: '/projects' },
    { text: 'Target Library', icon: <StarIcon />, path: '/library' },
    { text: 'Frame Library', icon: <CalibrationIcon />, path: '/calibration/library' },
    { text: 'Exoplanet Transits', icon: <PublicIcon />, path: '/exoplanets' },
    { text: 'Activity History', icon: <HistoryIcon />, path: '/activity-history' },
    { text: 'My Usage', icon: <TrendingUpIcon />, path: '/my-usage' },
    { text: 'Profile', icon: <PersonIcon />, path: '/profile' },
    { text: 'Contact Support', icon: <HelpOutlineIcon />, path: '/support' },
  ];

  const adminItems = [
    { text: 'Admin Dashboard', icon: <AdminIcon />, path: '/admin/dashboard' },
    { text: 'Observatories', icon: <ObservatoryIcon />, path: '/admin/observatories' },
    { text: 'Observations', icon: <ListIcon />, path: '/admin/observations' },
    { text: 'Organizations', icon: <OrganizationIcon />, path: '/admin/organizations' },
    { text: 'Projects', icon: <ProjectsIcon />, path: '/admin/projects' },
    { text: 'Users', icon: <UsersIcon />, path: '/admin/users' },
    { text: 'API Keys', icon: <ApiKeyIcon />, path: '/admin/api-keys' },
    { text: 'System Settings', icon: <SettingsIcon />, path: '/admin/settings' },
    { text: 'System Status', icon: <SystemStatusIcon />, path: '/admin/status' },
    { text: 'Database Integrity', icon: <IntegrityIcon />, path: '/admin/integrity' },
    { text: 'Backup Management', icon: <BackupIcon />, path: '/admin/backups' },
    { text: 'Analytics', icon: <AnalyticsIcon />, path: '/admin/analytics' },
    { text: 'Log Viewer', icon: <LogsIcon />, path: '/admin/logs' },
    { text: 'Log Settings', icon: <StorageIcon />, path: '/admin/logs/settings' },
    { text: 'Plugin Releases', icon: <PluginReleasesIcon />, path: '/admin/plugin-releases' },
    { text: 'Calibration Library', icon: <CalibrationIcon />, path: '/admin/calibration/library' },
  ];

  const reportingItems = [
    { text: 'Usage Summary', icon: <TrendingUpIcon />, path: '/admin/billing/dashboard' },
    { text: 'Usage Explorer', icon: <SearchIcon />, path: '/admin/billing/explorer' },
    { text: 'User Usage', icon: <UsersIcon />, path: '/admin/billing/users' },
    { text: 'Project Usage', icon: <ProjectsIcon />, path: '/admin/billing/projects' },
    { text: 'Observatory Utilization', icon: <StorageIcon />, path: '/admin/billing/observatories' },
  ];

  const renderSection = (title, items, isOpen, setOpen) => (
    <>
      <ListItem disablePadding>
        <ListItemButton onClick={() => setOpen(!isOpen)} sx={{ py: 0.5 }}>
          <ListItemText
            primary={
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: 0.5 }}>
                {title}
              </Typography>
            }
          />
          {isOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </ListItemButton>
      </ListItem>
      <Collapse in={isOpen} timeout="auto" unmountOnExit>
        <List disablePadding>
          {items.map((item) => (
            <ListItem key={item.path} disablePadding>
              <ListItemButton onClick={() => handleNav(item.path)} sx={{ pl: 3, py: 0.25 }}>
                <ListItemIcon sx={{ minWidth: 32 }}>{item.icon}</ListItemIcon>
                <ListItemText primary={item.text} primaryTypographyProps={{ variant: 'body2', fontSize: '0.8rem' }} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Collapse>
    </>
  );

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: 280,
          backgroundColor: 'background.paper',
          borderLeft: '1px solid rgba(255,255,255,0.08)',
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, pb: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          Navigation
        </Typography>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      <Divider />

      <List sx={{ py: 0 }}>
        {renderSection('USER', userItems, userOpen, setUserOpen)}
        <Divider sx={{ my: 0.5 }} />

        {(adminConfig.isFullAdmin || adminConfig.showAdminMenu) && (
          <>
            {renderSection('ADMINISTRATION', adminItems, adminOpen, setAdminOpen)}
            <Divider sx={{ my: 0.5 }} />
            {renderSection('REPORTING', reportingItems, reportingOpen, setReportingOpen)}
          </>
        )}
      </List>
    </Drawer>
  );
};

export default AdminDrawer;
